import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, CreditCard, Lock, MessageCircle, QrCode, RotateCcw, ShieldCheck, XCircle } from 'lucide-react';
import { getAssetUrl } from '../utils/assetUrl';
import { formatPrice } from '../utils/formatCurrency';
import { Button, IconButton } from '../components/UI';

type Currency = 'EUR' | 'BRL';

interface Offer {
  slug: string;
  title: string;
  description: string;
  terms?: string;
  video?: { url: string; posterUrl: string } | null;
  interval: 'month' | 'year';
  prices: Record<Currency, number | null>;
  suggestedCurrency: Currency;
  pixAvailable?: boolean;
  closed: boolean;
  lead: { name: string; email: string } | null;
}


// /comunidade/:slug — oferta da comunidade paga. Chega pelo {comunidade} dos e-mails de nutrição
// (?lead=<token> pré-preenche nome/e-mail) e manda para o Stripe Checkout. ?ok=1 = voltou pago.
const CommunityOffer: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'pt').slice(0, 2);
  const leadToken = searchParams.get('lead') || '';
  const paid = searchParams.get('ok') === '1';

  const [state, setState] = useState<'loading' | 'ready' | 'notFound'>('loading');
  const [offer, setOffer] = useState<Offer | null>(null);
  const [currency, setCurrency] = useState<Currency>('EUR');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ lang, ...(leadToken && { lead: leadToken }) });
    axios.get<Offer>(`/community/${slug}/offer?${params}`)
      .then((res) => {
        if (cancelled) return;
        setOffer(res.data);
        setCurrency(res.data.suggestedCurrency);
        setName((prev) => prev || res.data.lead?.name || '');
        setEmail((prev) => prev || res.data.lead?.email || '');
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('notFound'); });
    return () => { cancelled = true; };
  }, [slug, lang, leadToken]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t('community.offer.errorEmail'));
      return;
    }
    if (offer?.terms && !acceptedTerms) {
      setError(t('community.offer.errorTerms'));
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`/community/${slug}/checkout`, {
        currency,
        email: email.trim(),
        name: name.trim(),
        lead: leadToken || undefined,
        lang,
        acceptTerms: acceptedTerms
      });
      window.location.href = res.data.url;
    } catch (err: any) {
      const data = err.response?.data;
      setError(t(data?.alreadyActive ? 'community.offer.alreadyActive' : data?.termsRequired ? 'community.offer.errorTerms' : 'community.offer.errorGeneric'));
      setSubmitting(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-16">
      <div className="card max-w-lg w-full">{children}</div>
    </div>
  );

  if (paid) {
    return shell(
      <div className="text-center">
        <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('community.offer.successTitle')}</h1>
        <p className="text-gray-600 mb-6">{t('community.offer.successMessage')}</p>
        <Button to="/">{t('community.offer.backHome')}</Button>
      </div>
    );
  }

  if (state === 'loading') {
    return shell(<div className="h-40 animate-pulse bg-gray-100 rounded-lg" />);
  }

  if (state === 'notFound' || !offer) {
    return shell(
      <div className="text-center">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('community.offer.notFoundTitle')}</h1>
        <p className="text-gray-600 mb-6">{t('community.offer.notFoundMessage')}</p>
        <Button to="/">{t('community.offer.backHome')}</Button>
      </div>
    );
  }

  const currencies = (['EUR', 'BRL'] as Currency[]).filter((c) => offer.prices[c] !== null);
  const price = offer.prices[currency];
  const withPix = currency === 'BRL' && !!offer.pixAvailable;
  const perInterval = offer.interval === 'year' ? t('community.offer.perYear') : t('community.offer.perMonth');
  const needsTerms = !!offer.terms;

  return shell(
    <>
      <div className="flex items-center gap-3 mb-4">
        <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#25D366]/10 shrink-0">
          <MessageCircle className="w-6 h-6 text-[#25D366]" />
        </span>
        <h1 className="text-2xl font-bold text-gray-900">{offer.title}</h1>
      </div>
      {offer.description && <p className="text-gray-700 whitespace-pre-line mb-6">{offer.description}</p>}

      {offer.video?.url && (
        <video
          key={offer.video.url}
          src={getAssetUrl(offer.video.url)}
          poster={offer.video.posterUrl ? getAssetUrl(offer.video.posterUrl) : undefined}
          controls
          playsInline
          preload="metadata"
          className="w-full rounded-lg bg-black aspect-video mb-6"
        />
      )}

      {offer.closed ? (
        <div className="text-center border-t pt-6">
          <Lock className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('community.offer.closedTitle')}</h2>
          <p className="text-gray-600">{t('community.offer.closedMessage')}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 border-t pt-6">
          {currencies.length > 1 && (
            <div role="radiogroup" aria-label={t('community.offer.currency')} className="grid grid-cols-2 gap-2">
              {currencies.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={currency === c}
                  onClick={() => setCurrency(c)}
                  className={`rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-colors ${
                    currency === c ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {c === 'EUR' ? '€ EUR' : 'R$ BRL'}
                </button>
              ))}
            </div>
          )}

          {price !== null && (
            <p className="text-center">
              <span className="text-4xl font-bold text-gray-900">{formatPrice(price, currency, lang)}</span>
              <span className="text-gray-500 ml-1">{perInterval}</span>
            </p>
          )}

          <div>
            <label htmlFor="community-name" className="block text-sm font-medium text-gray-700 mb-1">{t('community.offer.name')}</label>
            <input
              id="community-name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="field"
            />
          </div>
          <div>
            <label htmlFor="community-email" className="block text-sm font-medium text-gray-700 mb-1">{t('community.offer.email')}</label>
            <input
              id="community-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field"
            />
            <p className="text-xs text-gray-500 mt-1">{t('community.offer.emailHint')}</p>
          </div>

          <div className="flex gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
            <RotateCcw className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-green-900">{t('community.offer.refundTitle')}</p>
              <p className="text-green-800">{t('community.offer.refundText')}</p>
            </div>
          </div>

          {needsTerms && (
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => { setAcceptedTerms(e.target.checked); if (e.target.checked) setError(''); }}
                className="check-input mt-0.5"
              />
              <span>
                {t('community.offer.acceptTermsBefore')}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setTermsOpen(true); }}
                  className="text-primary-700 underline hover:text-primary-800"
                >
                  {t('community.offer.termsLink')}
                </button>
              </span>
            </label>
          )}

          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

          <Button className="w-full" type="submit" disabled={submitting || price === null || (needsTerms && !acceptedTerms)}>
            <CreditCard className="w-5 h-5" />
            {withPix && <QrCode className="w-5 h-5" />}
            {submitting ? t('community.offer.redirecting') : t(withPix ? 'community.offer.payCardOrPix' : 'community.offer.payCard')}
          </Button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
            <ShieldCheck className="w-4 h-4" />
            {t('community.offer.secure')}
          </p>
        </form>
      )}

      {termsOpen && offer.terms && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="community-terms-title"
          onClick={() => setTermsOpen(false)}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h2 id="community-terms-title" className="text-lg font-semibold text-gray-900">{t('community.offer.termsModalTitle')}</h2>
              <IconButton action="close" label={t('community.offer.termsClose')} onClick={() => setTermsOpen(false)} />
            </div>
            <div className="overflow-y-auto px-5 py-4 text-sm text-gray-700 whitespace-pre-line">{offer.terms}</div>
            <div className="form-actions border-t px-5 py-4">
              <Button variant="secondary" type="button" onClick={() => setTermsOpen(false)}>{t('community.offer.termsClose')}</Button>
              <Button
                type="button"
                onClick={() => { setAcceptedTerms(true); setError(''); setTermsOpen(false); }}>
                {t('community.offer.termsAcceptButton')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommunityOffer;
